import { useState } from 'react';
import { collection, addDoc, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import toast from 'react-hot-toast';

const AVAILABLE_COINS = [
  { value: 'RCN', label: 'RCN (Test)' },
  { value: 'FLCV', label: 'FLCV' },
  { value: 'PETCN', label: 'PETCN' },
  { value: 'FLCHU', label: 'FLCHU' },
  { value: 'TRLCN', label: 'TRLCN' },
  { value: 'SHPCN', label: 'SHPCN' },
  { value: 'VPVB', label: 'VPVB' },
  { value: 'GLDCN', label: 'GLDCN' },
  { value: 'CHRTC', label: 'CHRTC' },
  { value: 'HLTCN', label: 'HLTCN' }
];

export default function CreateWalletTransaction({ onClose, onSuccess }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [senderSearch, setSenderSearch] = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSender, setSelectedSender] = useState(null);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('RCN');
  const [description, setDescription] = useState('');

  const searchUsers = async (term) => {
    if (term.length < 3) {
      setSearchResults([]);
      return;
    }

    try {
      const usersRef = collection(db, 'walletUsers');
      const q = query(
        usersRef,
        where('email', '>=', term),
        where('email', '<=', term + '\uf8ff')
      );
      const snapshot = await getDocs(q);
      const users = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSearchResults(users);
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Hiba történt a felhasználók keresése során');
    }
  };

  const handleCreateTransaction = async (e) => {
    e.preventDefault();
    if (!selectedSender || !selectedRecipient || !amount) {
      toast.error('Kérjük, töltse ki az összes mezőt');
      return;
    }

    const selectedCoin = AVAILABLE_COINS.find(c => c.value === currency);
    const senderBalance = selectedSender.details?.balances?.[currency] || 0;
    if (parseFloat(amount) > parseFloat(senderBalance)) {
      toast.error(`Nincs elegendő ${currency} egyenleg a küldéshez`);
      return;
    }

    setLoading(true);
    try {
      const transactionData = {
        transaction_id: `ADMIN_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        amount: `${amount} ${currency}`,
        sender: {
          email: selectedSender.email,
          user_id: selectedSender.user_id,
        },
        recipient: {
          email: selectedRecipient.email,
          user_id: selectedRecipient.user_id,
        },
        created_at: new Date(),
        status: 'completed',
        type: 'admin_transfer',
        coin: currency,
        description: description.trim() || null,
        balance_before: {
          sender: senderBalance,
          recipient: selectedRecipient.details?.balances?.[currency] || 0
        }
      };

      await addDoc(collection(db, 'walletTransactions'), transactionData);

      const senderRef = doc(db, 'walletUsers', selectedSender.id);
      const recipientRef = doc(db, 'walletUsers', selectedRecipient.id);

      await Promise.all([
        updateDoc(senderRef, {
          [`details.balances.${currency}`]: (parseFloat(senderBalance) - parseFloat(amount)).toString()
        }),
        updateDoc(recipientRef, {
          [`details.balances.${currency}`]: (
            parseFloat(selectedRecipient.details?.balances?.[currency] || 0) + parseFloat(amount)
          ).toString()
        })
      ]);

      toast.success('Tranzakció sikeresen létrehozva');
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error('Error creating transaction:', error);
      toast.error('Hiba történt a tranzakció létrehozása során');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-4xl w-full">
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-xl font-semibold">Új Wallet Tranzakció</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <Icons.X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleCreateTransaction} className="space-y-6">
          {/* Küldő és Fogadó egymás mellett */}
          <div className="grid grid-cols-2 gap-4">
            {/* Küldő */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Küldő
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Keresés email alapján..."
                  value={senderSearch}
                  onChange={(e) => {
                    setSenderSearch(e.target.value);
                    searchUsers(e.target.value);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                />
                {searchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {searchResults.map(user => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => {
                          setSelectedSender(user);
                          setSenderSearch('');
                          setSearchResults([]);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-50"
                      >
                        <div className="text-sm font-medium">{user.email}</div>
                        <div className="text-xs text-gray-500">ID: {user.user_id}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedSender && (
                <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm font-medium">{selectedSender.email}</div>
                      <div className="text-xs text-gray-500">ID: {selectedSender.user_id}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedSender(null)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <Icons.X className="h-4 w-4" />
                    </button>
                  </div>
                  {/* Egyenlegek megjelenítése */}
                  <div className="mt-2 text-sm">
                    <div className="font-medium text-gray-700">Elérhető egyenlegek:</div>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      {AVAILABLE_COINS.map(coin => {
                        const balance = selectedSender.details?.balances?.[coin.value] || '0';
                        return (
                          <div key={coin.value} className="text-gray-600">
                            {coin.label}: {balance}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Fogadó */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fogadó
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Keresés email alapján..."
                  value={recipientSearch}
                  onChange={(e) => {
                    setRecipientSearch(e.target.value);
                    searchUsers(e.target.value);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                />
                {searchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {searchResults.map(user => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => {
                          setSelectedRecipient(user);
                          setRecipientSearch('');
                          setSearchResults([]);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-gray-50"
                      >
                        <div className="text-sm font-medium">{user.email}</div>
                        <div className="text-xs text-gray-500">ID: {user.user_id}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedRecipient && (
                <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-sm font-medium">{selectedRecipient.email}</div>
                      <div className="text-xs text-gray-500">ID: {selectedRecipient.user_id}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedRecipient(null)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <Icons.X className="h-4 w-4" />
                    </button>
                  </div>
                  {/* Egyenlegek megjelenítése */}
                  <div className="mt-2 text-sm">
                    <div className="font-medium text-gray-700">Elérhető egyenlegek:</div>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      {AVAILABLE_COINS.map(coin => {
                        const balance = selectedRecipient.details?.balances?.[coin.value] || '0';
                        return (
                          <div key={coin.value} className="text-gray-600">
                            {coin.label}: {balance}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Összeg és Coin egymás mellett */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Coin
              </label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              >
                {AVAILABLE_COINS.map(coin => (
                  <option key={coin.value} value={coin.value}>
                    {coin.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Összeg
              </label>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>

          {/* Új mező a megjegyzésnek */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Megjegyzés
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 resize-none"
              placeholder="Opcionális megjegyzés a tranzakcióhoz..."
            />
          </div>

          {/* Küldő egyenlege */}
          {selectedSender && (
            <div className="text-sm text-gray-500">
              Küldő egyenlege: {selectedSender.details?.balances?.[currency] || 0} {currency}
            </div>
          )}

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Mégse
            </button>
            <button
              type="submit"
              disabled={loading || !selectedSender || !selectedRecipient || !amount}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? (
                <Icons.Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                'Tranzakció létrehozása'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 